import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from 'react-router-dom';
import styles from "./Profile.module.css";
import Dropdown from "../../components/ui/Dropdown";
import InputField from "../../components/ui/InputField";
import PhoneInput from "../../components/ui/PhoneInput";
import FilePhoto from "../../components/filePhoto/FilePhoto";
import { useScreenSize } from "../../components/auxiliaryFunctions";
import { UserContext } from "../../context/UserContext";
import { ModalChangeEmail } from "../../components/modalInfo/ModalInfo";
import { ProfileSkeleton } from "../../components/skeleton/profile/ProfileSkeleton";
import { PencilSvg } from "../../img/Image";
import DatePicker from "react-datepicker";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import { subYears } from "date-fns";
import ru from "date-fns/locale/ru";
import countries from "../../components/countries.json";
import "react-datepicker/dist/react-datepicker.css";
import "./Profile.css";
import Notification from "../../components/notification/Notification";
import NotificationCompleted from "../../components/notification/NotificationCompleted";

import { useApi } from '../../utils/useApi';
import { createAuthApi } from './api';

registerLocale("ru", ru);
setDefaultLocale("ru");
const currentDate = new Date();
const minDate = new Date(1900, 0, 1);
const maxDate = subYears(currentDate, 10);

const CheckIcon = ({ className }) => (
    <svg
        width="26"
        height="26"
        viewBox="0 0 26 26"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
    >
        <circle cx="13" cy="13" r="11.25" stroke="#33363F" strokeWidth="2" />
        <path d="M8 13L11.75 16.75L18 9.25" stroke="#33363F" strokeWidth="2" />
    </svg>
);

const Profile = ({ isToken }) => {
    const { isMobile } = useScreenSize();
    const [userInfo, setUserInfo] = useContext(UserContext);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const { customFetch } = useApi();
    const authApi = createAuthApi(customFetch);
    const [isFocused, setIsFocused] = useState(false);
    const [errors, setErrors] = useState({});
    const initialDataRef = useRef(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [showNotificationCompleted, setShowNotificationCompleted] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);

    const countriesData = Array.isArray(countries) ? countries : [countries];
    const [userPhotoId, setUserPhotoId] = useState(null);
    const [userData, setUserDate] = useState({
        id: "",
        surname: "",
        name: "",
        patronymic: "",
        email: "",
        phone: "",
        sex: null,
        birthday: null,
        country: null,
        region: "",
        city: "",
        photo: { id: "", link: "" },
        organization: "",
        position: "",
        education: [{ organization: "", speciality: "" }]
    });

    // Проверка обязательных полей
    const requiredFields = [
        'surname', 'name', 'phone', 'sex', 'birthday',
        'country', 'region', 'city', 'organization', 'position'
    ];

    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        // Проверка основных обязательных полей
        requiredFields.forEach(field => {
            if (!userData[field]) {
                newErrors[field] = 'Это поле обязательно для заполнения';
                isValid = false;
            }
        });

        // Специальная проверка для телефона
        if (userData.phone && (userData.phone.length < 11 || userData.phone.length > 12)) {
            newErrors.phone = 'Номер телефона должен содержать 11-12 цифр';
            isValid = false;
        }

        // Проверка образования
        userData.education.forEach((edu, index) => {
            if (!edu.organization) {
                newErrors[`education_org_${index}`] = 'Укажите учебное заведение';
                isValid = false;
            }
            if (!edu.speciality) {
                newErrors[`education_spec_${index}`] = 'Укажите специальность';
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            setShowNotification(true);
            return;
        }
        setIsSaving(true);
        try {
            const result = await authApi.updateProfile(userData, userPhotoId);
            if (result) {
                // Обновляем исходные данные после успешного сохранения
                initialDataRef.current = JSON.parse(JSON.stringify({
                    ...userData,
                    photo: { ...userData.photo } // Клонируем photo отдельно
                }));
                setHasChanges(false);
                setShowNotificationCompleted(true);
            }
        } catch (error) {
            console.error('Ошибка при сохранении:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const changeEmail = async (email) => {
        try {
            const result = await authApi.changeEmail(email);
            if (result.success) {
                if (result.emailAlreadyUsed) {
                    return {
                        errorBool: true,
                        codeID: "",
                        errorText: "Данная почта уже используется"
                    }
                } else {
                    return {
                        errorBool: false,
                        codeID: result.codeID,
                        errorText: ""
                    }
                }
            }
        } catch (error) {
            console.error('Ошибка при сохранении:', error);
        }
    }

    const checkCodeOfEmailChanging = async (data) => {
        try {
            const result = await authApi.checkCodeOfEmailChanging(data);
            if (result.success) {
                if (result.isCodeCorrect) {
                    handleInputChange("email", data.email)
                    return true;
                } else {
                    return false;
                }
            }
        } catch (error) {
            console.error('Ошибка при сохранении:', error);
        }
    }

    const isFieldFilled = (value, phone = null) => {
        if (phone) {
            return value && (value.length === 11 || value.length === 12);
        } else {
            return value !== null && value !== undefined && value !== "";
        }
    };

    const handleAddEducation = () => {
        setUserDate(prev => ({
            ...prev,
            education: [...prev.education, { organization: "", speciality: "" }]
        }));
        setHasChanges(true);
    };

    const handleInputChange = (field, value) => {
        let processedValue = value;

        if (field === "birthday" && value) {
            const localDate = new Date(value);
            localDate.setHours(0, 0, 0, 0);
            const year = localDate.getFullYear();
            const month = String(localDate.getMonth() + 1).padStart(2, '0');
            const day = String(localDate.getDate()).padStart(2, '0');
            processedValue = `${year}-${month}-${day}T00:00:00`;
        }

        setUserDate(prev => ({
            ...prev,
            [field]: processedValue
        }));
        if (field != 'email') {
            setHasChanges(true);
        }
        // Сбрасываем ошибку при изменении поля
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const handleEducationChange = (index, field, value) => {
        const updatedEducations = [...userData.education];
        updatedEducations[index][field] = value;
        setUserDate(prev => ({
            ...prev,
            education: updatedEducations
        }));
        setHasChanges(true);

        // Сбрасываем ошибку при изменении поля
        const errorKey = `education_${field === 'organization' ? 'org' : 'spec'}_${index}`;
        if (errors[errorKey]) {
            setErrors(prev => ({
                ...prev,
                [errorKey]: ""
            }));
        }
    };

    const handleRemoveEducation = (index) => {
        setUserDate(prev => ({
            ...prev,
            education: prev.education.filter((_, i) => i !== index)
        }));
        setHasChanges(true);
    };

    const handleUpload = async (fileData) => {
        try {
            if (fileData) {
                const result = await authApi.createUserPhoto(fileData);
                if (result.success) {
                    setUserPhotoId(result.photo.id);
                    setHasChanges(true);
                }
            } else {
                setUserPhotoId(null);
                setHasChanges(true);
            }
        } catch (error) {
            console.error('Ошибка загрузки фото:', error);
        }
    };

    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() - 10);

    function delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    const getUserProfile = async () => {
        try {
            const result = await authApi.getProfile();
            if (result) {
                await delay(300);
                if (result.user.education.length == 0) {
                    result.user.education = [{ organization: "", speciality: "" }]
                }
                setUserDate(result.user);
                // Сохраняем исходные данные для сравнения
                initialDataRef.current = JSON.parse(JSON.stringify(result.user));
            }
        } catch (error) {
            console.error('Ошибка загрузки профиля:', error);
        } finally {
            setIsLoading(false);
        }
    }

    const handleLogout = async () => {
        try {
            const result = await authApi.logOut();
            if (result) {
                setUserInfo({ token: false, userId: "" });
                localStorage.setItem("UserToken", JSON.stringify({ token: false, userId: "" }));
                navigate('/login');
            }
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    useEffect(() => {
        if (isToken) {
            getUserProfile();
        } else {
            setIsLoading(false);
        }
    }, [isToken]);

    useEffect(() => {
        if (initialDataRef.current) {
            const { email: initialEmail, ...initialDataWithoutEmail } = initialDataRef.current;
            const { email: currentEmail, ...userDataWithoutEmail } = userData;
            const isDataChangedWithoutEmail = JSON.stringify(userDataWithoutEmail) !== JSON.stringify(initialDataWithoutEmail);
            setHasChanges(isDataChangedWithoutEmail);
        }
    }, [userData]);

    if (isLoading) {
        return <ProfileSkeleton isMobile={isMobile} />;
    }

    const ModalOpen = () => {
        setIsModalOpen(true);
    }

    return (
        <>
            <main className={styles.mainContent}>
                <div className={styles.stickyHeader}>
                    <div className={styles.header}>
                        <div className={styles.bottomSection}>
                            {!isMobile && (
                                <div className={styles.inputColumn}>
                                    <div className={styles.inputRow}>
                                        <div className={styles.inputFieldWrapper}>
                                            <h1 className={styles.title}>Профиль</h1>
                                        </div>
                                        <div className={styles.checkIconWrapper}>
                                        </div>
                                        <div className={styles.logoutButtonWrapper}>
                                            <button className={styles.logoutButton} onClick={handleLogout}>
                                                <svg className={styles.logoutIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M15 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H15M10 17L15 12M15 12L10 7M15 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className={styles.inputColumn}>
                                <div className={styles.inputRow}>
                                    <div className={styles.inputFieldWrapperButton}>
                                        <button
                                            className={`${styles.saveButton} ${!hasChanges ? styles.saveButtonDisabled : ''} ${isSaving ? styles.saveButtonLoading : ''}`}
                                            onClick={handleSave}
                                            disabled={!hasChanges || isSaving}
                                        >
                                            {isSaving ? (
                                                <div className={styles.savingContent}>
                                                    <span>СОХРАНЕНИЕ</span>
                                                    <div className={styles.loadingSpinner}></div>
                                                </div>
                                            ) : (
                                                "СОХРАНИТЬ"
                                            )}
                                        </button>
                                    </div>
                                    {isMobile == false && (
                                        <div className={styles.checkIconWrapper}>
                                        </div>
                                    )}

                                </div>
                            </div>
                            <div className={styles.inputFile}><div style={{ width: "300px" }}></div></div>
                        </div>
                    </div>
                </div>

                <div className={styles.bottomSection}>
                    <div className={styles.inputColumn}>
                        {/* Фамилия */}
                        <div className={styles.inputRow}>
                            <div className={styles.inputFieldWrapper}>
                                <InputField
                                    type="text"
                                    label="Фамилия*"
                                    value={userData.surname}
                                    onChange={(e) => handleInputChange("surname", e.target.value)}
                                    required={true}
                                    error={errors.surname}
                                />
                            </div>
                            <div className={styles.checkIconWrapper}>
                                <CheckIcon className={`${styles.customCheckIcon} ${isFieldFilled(userData.surname) ? styles.visible : ''}`} />
                            </div>
                        </div>

                        {/* Имя */}
                        <div className={styles.inputRow}>
                            <div className={styles.inputFieldWrapper}>
                                <InputField
                                    type="text"
                                    label="Имя*"
                                    value={userData.name}
                                    onChange={(e) => handleInputChange("name", e.target.value)}
                                    required={true}
                                    error={errors.name}
                                />
                            </div>
                            <div className={styles.checkIconWrapper}>
                                <CheckIcon className={`${styles.customCheckIcon} ${isFieldFilled(userData.name) ? styles.visible : ''}`} />
                            </div>
                        </div>

                        {/* Отчество */}
                        <div className={styles.inputRow}>
                            <div className={styles.inputFieldWrapper}>
                                <InputField
                                    type="text"
                                    label="Отчество"
                                    value={userData.patronymic}
                                    onChange={(e) => handleInputChange("patronymic", e.target.value)}
                                    required={true}
                                />
                            </div>
                            <div className={styles.checkIconWrapper}>
                                <CheckIcon className={`${styles.customCheckIcon} ${isFieldFilled(userData.patronymic) ? styles.visible : ''}`} />
                            </div>
                        </div>

                        {/* Email */}
                        <div className={styles.inputRow}>
                            <div className={styles.inputFieldWrapper}>
                                <InputField
                                    type="email"
                                    label="Email"
                                    value={userData.email}
                                    onChange={(e) => handleInputChange("email", e.target.value)}
                                    required={false}
                                    isEditing={true}
                                />
                            </div>
                            <div className={styles.checkIconWrapper}>
                                <button className={styles.buttonPencil} onClick={ModalOpen}>
                                    <PencilSvg className={styles.customPencilIcon} />
                                </button>
                            </div>
                        </div>

                        {/* Телефон */}
                        <div className={styles.inputRow}>
                            <div className={styles.inputFieldWrapper}>
                                <PhoneInput
                                    label="Номер телефона*"
                                    value={userData.phone}
                                    onChange={(phoneNumber) => handleInputChange("phone", phoneNumber)}
                                    required={true}
                                    error={errors.phone}
                                />
                            </div>
                            <div className={styles.checkIconWrapper}>
                                <CheckIcon className={`${styles.customCheckIcon} ${isFieldFilled(userData.phone, true) ? styles.visible : ''}`} />
                            </div>
                        </div>

                        {/* Пол */}
                        <div className={styles.genderContainer}>
                            <div className={styles.inputRow}>
                                <div className={styles.inputFieldWrapper}>
                                    <div className={styles.genderOptions}>
                                        <label className={styles.genderLabel}>Пол*</label>
                                        <label className={`${styles.genderOption} ${userData.sex === 'M' ? styles.selected : ''}`}>
                                            <input
                                                type="radio"
                                                name="gender"
                                                value="M"
                                                checked={userData.sex === "M"}
                                                onChange={() => handleInputChange("sex", "M")}
                                                className={styles.genderInput}
                                            />
                                            <span className={styles.customRadio}></span>
                                            Мужской
                                        </label>
                                        <label className={`${styles.genderOption} ${userData.sex === 'F' ? styles.selected : ''}`}>
                                            <input
                                                type="radio"
                                                name="gender"
                                                value="F"
                                                checked={userData.sex === "F"}
                                                onChange={() => handleInputChange("sex", "F")}
                                                className={styles.genderInput}
                                            />
                                            <span className={styles.customRadio}></span>
                                            Женский
                                        </label>
                                    </div>
                                    {errors.sex && <span className={styles.errorText}>{errors.sex}</span>}
                                </div>
                                <div className={styles.checkIconWrapper} style={{ marginBottom: 0 }}>
                                    <CheckIcon className={`${styles.customCheckIcon} ${isFieldFilled(userData.sex) ? styles.visible : ''}`} />
                                </div>
                            </div>
                        </div>

                        {/* Дата рождения */}

                        <div className={styles.datePickerContainer}>
                            <div className={styles.inputRow}>
                                <div className={styles.inputFieldWrapperDate}>
                                    <div className={styles.datePickerWrapper}>
                                        <div className={styles.inputWrapper}>
                                            <DatePicker
                                                selected={userData.birthday}
                                                onChange={(date) => handleInputChange("birthday", date)}
                                                dateFormat="dd.MM.yyyy"
                                                minDate={minDate}
                                                maxDate={maxDate}
                                                placeholderText=""
                                                showYearDropdown
                                                showMonthDropdown
                                                dropdownMode="select"
                                                locale="ru"
                                                className={`${styles.datePickerInput} ${userData.birthday ? styles.hasValue : ''} ${errors.birthday ? styles.inputError : ''}`}
                                                popperClassName={styles.customPopper}
                                                onFocus={() => setIsFocused(true)}
                                                onBlur={() => !userData.birthday && setIsFocused(false)}
                                            />
                                            <label
                                                className={`${styles.label} ${isFocused || userData.birthday ? styles.labelFloating : ''}`}
                                            >
                                                Дата рождения*
                                            </label>
                                            <svg
                                                className={styles.calendarIcon}
                                                viewBox="0 0 24 24"
                                                width="18"
                                                height="18"
                                            >
                                                <path fill="currentColor" d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm-8 4H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z" />
                                            </svg>
                                        </div>
                                    </div>
                                    {errors.birthday && <span className={styles.errorText}>{errors.birthday}</span>}
                                </div>
                                <div className={styles.checkIconWrapper} style={{ marginBottom: 0 }}>
                                    <CheckIcon className={`${styles.customCheckIcon} ${isFieldFilled(userData.birthday) ? styles.visible : ''}`} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.inputColumn}>
                        {/* Страна */}
                        <div className={styles.inputRow}>
                            <div className={styles.inputFieldWrapper}>
                                <Dropdown
                                    label="Страна*"
                                    placeholder="Страна*"
                                    value={userData.country}
                                    options={countriesData}
                                    onSelect={(item) => handleInputChange('country', item)}
                                    error={errors.country} // передаем строку с ошибкой или пустую строку
                                    required={true}
                                />
                            </div>
                            <div className={styles.checkIconWrapper}>
                                <CheckIcon className={`${styles.customCheckIcon} ${isFieldFilled(userData.country) ? styles.visible : ''}`} />
                            </div>
                        </div>

                        {/* Регион */}
                        <div className={styles.inputRow}>
                            <div className={styles.inputFieldWrapper}>
                                <InputField
                                    type="text"
                                    label="Регион*"
                                    value={userData.region}
                                    onChange={(e) => handleInputChange("region", e.target.value)}
                                    required={true}
                                    error={errors.region}
                                />
                            </div>
                            <div className={styles.checkIconWrapper}>
                                <CheckIcon className={`${styles.customCheckIcon} ${isFieldFilled(userData.region) ? styles.visible : ''}`} />
                            </div>
                        </div>

                        {/* Город */}
                        <div className={styles.inputRow}>
                            <div className={styles.inputFieldWrapper}>
                                <InputField
                                    type="text"
                                    label="Город*"
                                    value={userData.city}
                                    onChange={(e) => handleInputChange("city", e.target.value)}
                                    required={true}
                                    error={errors.city}
                                />
                            </div>
                            <div className={styles.checkIconWrapper}>
                                <CheckIcon className={`${styles.customCheckIcon} ${isFieldFilled(userData.city) ? styles.visible : ''}`} />
                            </div>
                        </div>

                        {/* Место работы */}
                        <div className={styles.inputRow}>
                            <div className={styles.inputFieldWrapper}>
                                <InputField
                                    type="text"
                                    label="Место работы*"
                                    value={userData.organization}
                                    onChange={(e) => handleInputChange("organization", e.target.value)}
                                    required={true}
                                    error={errors.organization}
                                />
                            </div>
                            <div className={styles.checkIconWrapper}>
                                <CheckIcon className={`${styles.customCheckIcon} ${isFieldFilled(userData.organization) ? styles.visible : ''}`} />
                            </div>
                        </div>

                        {/* Должность */}
                        <div className={styles.inputRow}>
                            <div className={styles.inputFieldWrapper}>
                                <InputField
                                    type="text"
                                    label="Должность*"
                                    value={userData.position}
                                    onChange={(e) => handleInputChange("position", e.target.value)}
                                    required={true}
                                    error={errors.position}
                                />
                            </div>
                            <div className={styles.checkIconWrapper}>
                                <CheckIcon className={`${styles.customCheckIcon} ${isFieldFilled(userData.position) ? styles.visible : ''}`} />
                            </div>
                        </div>

                        {/* Образование */}
                        <div className={styles.inputRow}>
                            <div className={styles.inputFieldWrapper}>
                                <div className={styles.educationHeader}>
                                    <label className={styles.genderLabel}>Образование*</label>
                                    <button
                                        onClick={handleAddEducation}
                                        className={styles.addEducationButton}
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                        </div>

                        {userData.education.map((edu, index) => (
                            <div key={index} className={styles.educationItem}>
                                {/* Организация */}
                                <div className={styles.inputRow}>
                                    <div className={styles.inputFieldWrapper}>
                                        <InputField
                                            type="text"
                                            label="Организация*"
                                            value={edu.organization}
                                            required={true}
                                            onChange={(e) => handleEducationChange(index, "organization", e.target.value)}
                                            error={errors[`education_org_${index}`]}
                                        />
                                    </div>
                                    <div className={styles.checkIconWrapper}>
                                        <CheckIcon className={`${styles.customCheckIcon} ${isFieldFilled(edu.organization) ? styles.visible : ''}`} />
                                    </div>
                                </div>

                                {/* Специальность */}
                                <div className={styles.inputRow}>
                                    <div className={styles.inputFieldWrapper}>
                                        <InputField
                                            type="text"
                                            label="Специальность*"
                                            value={edu.speciality}
                                            required={true}
                                            onChange={(e) => handleEducationChange(index, "speciality", e.target.value)}
                                            error={errors[`education_spec_${index}`]}
                                        />
                                    </div>
                                    <div className={styles.checkIconWrapper}>
                                        <CheckIcon className={`${styles.customCheckIcon} ${isFieldFilled(edu.speciality) ? styles.visible : ''}`} />
                                    </div>
                                </div>
                                {index != 0 && (
                                    <button
                                        onClick={() => handleRemoveEducation(index)}
                                        className={styles.deleteEducationButton}
                                    >
                                        Удалить
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className={styles.inputFile}>
                        <FilePhoto
                            url={userData.photo?.link}
                            onUpload={handleUpload}
                        />
                    </div>
                </div>
            </main>
            {isModalOpen && (
                <ModalChangeEmail isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} changeEmail={changeEmail} confirmEmail={checkCodeOfEmailChanging} />
            )}
            <Notification
                message="Пожалуйста, заполните все обязательные поля"
                visible={showNotification}
                onClose={() => setShowNotification(false)}
            />
            <NotificationCompleted
                message="Данные успешно сохранены!"
                visible={showNotificationCompleted}
                onClose={() => setShowNotificationCompleted(false)}
            />
        </>
    );
};

export default Profile;